import { useState, useEffect, useCallback } from 'react';
import { MessageSquare, RefreshCw, Send, ExternalLink } from 'lucide-react';
import { contentApi, type ContentComment } from '../../api/content';

interface CommentsSectionProps {
  projectId: string;
  itemId: string;
}

export function CommentsSection({ projectId, itemId }: CommentsSectionProps) {
  const [comments, setComments] = useState<ContentComment[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [replyingTo, setReplyingTo] = useState<string | null>(null);
  const [replyText, setReplyText] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const fetchComments = useCallback(() => {
    setLoading(true);
    contentApi.getComments(projectId, itemId)
      .then(data => setComments(data.comments))
      .catch(() => setComments([]))
      .finally(() => setLoading(false));
  }, [projectId, itemId]);

  useEffect(() => { fetchComments(); }, [fetchComments]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      await contentApi.syncComments(projectId, itemId);
      fetchComments();
    } catch (err) {
      console.error('Failed to sync comments', err);
    } finally {
      setSyncing(false);
    }
  };

  const handleReply = async (commentId: string) => {
    if (!replyText.trim()) return;
    setSubmitting(true);
    try {
      const result = await contentApi.replyToComment(projectId, itemId, commentId, replyText.trim());
      setComments(prev => prev.map(c => c.id === commentId ? result.comment : c));
      setReplyingTo(null);
      setReplyText('');
    } catch (err) {
      console.error('Failed to reply to comment', err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-xs font-semibold text-muted uppercase tracking-wider flex items-center gap-1.5">
          <MessageSquare size={12} />
          Comments
          {comments.length > 0 && (
            <span className="text-[10px] font-medium px-1.5 py-0.5 bg-surface-subtle text-muted rounded-full">
              {comments.length}
            </span>
          )}
        </h3>
        <button
          onClick={handleSync}
          disabled={syncing}
          className="flex items-center gap-1 text-[11px] text-coral-500 hover:text-coral-600 font-medium disabled:opacity-50"
          title="Sync comments from LinkedIn"
        >
          <RefreshCw size={11} className={syncing ? 'animate-spin' : ''} />
          Sync
        </button>
      </div>

      {loading ? (
        <div className="bg-surface-muted rounded-lg p-4 space-y-3">
          {[1, 2].map(i => (
            <div key={i} className="animate-pulse flex gap-2">
              <div className="w-7 h-7 rounded-full bg-surface-subtle shrink-0" />
              <div className="flex-1 space-y-1.5">
                <div className="h-3 w-24 bg-surface-subtle rounded" />
                <div className="h-3 w-full bg-surface-subtle rounded" />
              </div>
            </div>
          ))}
        </div>
      ) : comments.length === 0 ? (
        <div className="bg-surface-muted rounded-lg p-6 text-center">
          <MessageSquare className="mx-auto text-subtle mb-2" size={24} />
          <p className="text-xs text-subtle">No comments yet</p>
          <p className="text-[10px] text-subtle mt-1">
            Comments from LinkedIn will appear here once Voyager integration is connected.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {comments.map(comment => (
            <div key={comment.id} className="bg-white rounded-lg border border-line p-3">
              {/* Comment header */}
              <div className="flex items-center gap-2 mb-1.5">
                <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center text-[10px] font-bold text-blue-600 shrink-0">
                  {(comment.author_name || '?').charAt(0).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs font-medium text-text truncate">
                      {comment.author_name || 'Unknown'}
                    </span>
                    {comment.author_profile_url && (
                      <a
                        href={comment.author_profile_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-400 hover:text-blue-500"
                      >
                        <ExternalLink size={9} />
                      </a>
                    )}
                  </div>
                  {comment.commented_at && (
                    <div className="text-[10px] text-subtle">
                      {new Date(comment.commented_at).toLocaleDateString('en-US', {
                        month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
                      })}
                    </div>
                  )}
                </div>
              </div>

              {/* Comment body */}
              <p className="text-xs text-muted leading-relaxed">{comment.comment_text}</p>

              {/* Reply (if exists) */}
              {comment.reply_text && (
                <div className="mt-2 ml-4 pl-3 border-l-2 border-coral-200">
                  <div className="text-[10px] text-coral-500 font-medium mb-0.5">Your reply</div>
                  <p className="text-xs text-muted">{comment.reply_text}</p>
                </div>
              )}

              {/* Reply button / form */}
              {!comment.reply_text && (
                <>
                  {replyingTo === comment.id ? (
                    <div className="mt-2 flex gap-1.5">
                      <input
                        type="text"
                        value={replyText}
                        onChange={e => setReplyText(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && handleReply(comment.id)}
                        placeholder="Write a reply..."
                        className="flex-1 text-xs border border-line rounded-md px-2 py-1.5
                          focus:outline-none focus:ring-1 focus:ring-coral-500/20"
                        autoFocus
                      />
                      <button
                        onClick={() => handleReply(comment.id)}
                        disabled={!replyText.trim() || submitting}
                        className="p-1.5 bg-coral-500 text-white rounded-md hover:bg-coral-600
                          disabled:opacity-50 transition-colors"
                      >
                        <Send size={11} />
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => { setReplyingTo(comment.id); setReplyText(''); }}
                      className="mt-1.5 text-[11px] text-coral-500 hover:text-coral-600 font-medium"
                    >
                      Reply
                    </button>
                  )}
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
